/**
 * Cost Details Table Component
 * Displays the breakdown by individual pay rules with filters
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, MapPin, Filter, X, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const CostDetailsTable = ({
  filteredDetails,
  totalDetails,
  hasActiveFilters,
  searchTerm,
  setSearchTerm,
  selectedType,
  setSelectedType,
  selectedState,
  setSelectedState,
  selectedCategory,
  setSelectedCategory,
  filterOptions,
  clearFilters,
  sortConfig,
  requestSort,
  formatCurrency,
  onRowClick
}) => {
  // Get sort icon for header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === "desc" 
      ? <ArrowDown className="w-3 h-3 ml-1" />
      : <ArrowUp className="w-3 h-3 ml-1" />;
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-sm font-medium">Cost Details</CardTitle>
            <CardDescription className="text-xs">
              Breakdown by individual pay rules - click a row to view details
              {hasActiveFilters && ` (${filteredDetails.length} of ${totalDetails} shown)`}
            </CardDescription>
          </div>
        </div>
        
        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <div className="relative flex-1 min-w-[150px] max-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <Input
              placeholder="Search rules..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-7 pl-7 text-xs"
              data-testid="cost-search-input"
            />
          </div>
          
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-[90px] h-7 text-xs" data-testid="type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="risk">Risk</SelectItem>
              <SelectItem value="overpay">Over</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedState} onValueChange={setSelectedState}>
            <SelectTrigger className="w-[100px] h-7 text-xs" data-testid="state-filter">
              <MapPin className="w-3 h-3 mr-1" />
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {filterOptions.states.map(state => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[110px] h-7 text-xs" data-testid="category-filter">
              <Filter className="w-3 h-3 mr-1" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {filterOptions.categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearFilters}
              className="h-7 px-2 text-xs"
            >
              <X className="w-3 h-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[250px]">
          <table className="data-table text-sm">
            <thead className="sticky top-0 bg-card z-10">
              <tr>
                <th 
                  className="py-2 cursor-pointer hover:bg-muted/50"
                  onClick={() => requestSort("type")}
                >
                  <div className="flex items-center">Type {getSortIcon("type")}</div>
                </th>
                <th 
                  className="py-2 cursor-pointer hover:bg-muted/50"
                  onClick={() => requestSort("ruleName")}
                >
                  <div className="flex items-center">Pay Rule {getSortIcon("ruleName")}</div>
                </th>
                <th 
                  className="py-2 cursor-pointer hover:bg-muted/50"
                  onClick={() => requestSort("company")}
                >
                  <div className="flex items-center">Company {getSortIcon("company")}</div>
                </th>
                <th 
                  className="py-2 cursor-pointer hover:bg-muted/50"
                  onClick={() => requestSort("state")}
                >
                  <div className="flex items-center">State {getSortIcon("state")}</div>
                </th>
                <th 
                  className="py-2 cursor-pointer hover:bg-muted/50"
                  onClick={() => requestSort("category")}
                >
                  <div className="flex items-center">Category {getSortIcon("category")}</div>
                </th>
                <th 
                  className="py-2 cursor-pointer hover:bg-muted/50"
                  onClick={() => requestSort("employees")}
                >
                  <div className="flex items-center">Employees {getSortIcon("employees")}</div>
                </th>
                <th 
                  className="py-2 text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => requestSort("amount")}
                >
                  <div className="flex items-center justify-end">Cost {getSortIcon("amount")}</div>
                </th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filteredDetails.length > 0 ? (
                filteredDetails.map((detail) => (
                  <tr 
                    key={`${detail.payRuleId}-${detail.type}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onRowClick(detail)}
                    data-testid={`cost-row-${detail.payRuleId}`}
                  >
                    <td className="py-1.5">
                      <Badge className={detail.type === "risk" ? "bg-red-500 text-white text-[10px]" : "bg-amber-500 text-white text-[10px]"}>
                        {detail.type === "risk" ? "Risk" : "Over"}
                      </Badge>
                    </td>
                    <td className="py-1.5 font-medium truncate max-w-[150px]">{detail.ruleName}</td>
                    <td className="py-1.5">{detail.company}</td>
                    <td className="py-1.5">{detail.state || "-"}</td>
                    <td className="py-1.5">{detail.category}</td>
                    <td className="py-1.5">{detail.employees}</td>
                    <td className={`py-1.5 text-right font-semibold ${detail.type === "risk" ? "text-red-600" : "text-amber-600"}`}>
                      {formatCurrency(detail.amount)}
                    </td>
                    <td className="py-1.5">
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-muted-foreground">
                    {hasActiveFilters ? "No matching cost details found" : "No cost data available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default CostDetailsTable;
